import { HTTPRequestMethod } from 'lib-shared-legacy-types';
import _groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { findScopeVariableMatches } from 'components/powerFlowShared/helpers/findScopeVariableMatches';
import { flowNodeColor, FlowType } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';

import IntegrationModal from '../../../containers/modals/integration';
import { ContentType } from '../../types';

export type Announcement = {
  file: {
    author: string;
    filename?: string;
    url: string;
    pretty_name?: string;
  } | null;
};

export type Variable = {
  id: string;
  path: string;
  type: string;
};

export type Payload = {
  announcement?: Announcement;
  body: string;
  contentType: ContentType;
  headers: Record<string, string>;
  httpRequestType: HTTPRequestMethod;
  nextNodeId: null | string;
  url: string;
  variables: Variable[];
};

type IntegrationNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type IntegrationNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: IntegrationNodeAttributes['translatePayload'] = (
  node,
) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId || uuid(),
    },
  ];
};

const updateConnection: IntegrationNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return { ...node, payload };
};

const translateScopeVariables: IntegrationNodeAttributes['translateScopeVariables'] =
  (node) => {
    const { variables = [], url, body } = node.payload;

    const needs = [
      ...findScopeVariableMatches(url),
      ...findScopeVariableMatches(body),
    ];

    const grants = variables.reduce<string[]>((acc, { id }) => {
      if (id) {
        acc.push(id);
      }

      return acc;
    }, []);

    return {
      needs,
      grants,
    };
  };

const integrationDefaultHelpText =
  'Integrate third-party systems or your own API to route conversations based on relevant customer information in Dixa (e.g. previous orders, tracking links, etc.).';

const createIntegrationNode = (
  flowType: FlowType,
  attributes?: {
    helpText: IntegrationNodeAttributes['helpText'];
  },
): IntegrationNodeAttributes => ({
  iconName: 'bolt',
  label: 'Integration',
  color: hexStringToNumber(flowNodeColor.blue),
  helpText: attributes?.helpText ?? integrationDefaultHelpText,
  translateScopeVariables,
  translatePayload,
  updateConnection,
  NodeContent: IntegrationNode,
  ActionModal: IntegrationModal(flowType),
});

export const IntegrationNodePhoneAttributes = createIntegrationNode(
  FlowType.FLOW_TYPE_CALL,
);

export const IntegrationNodeEmailAttributes = createIntegrationNode(
  FlowType.FLOW_TYPE_EMAIL,
);

export const IntegrationNodeMessengerAttributes = createIntegrationNode(
  FlowType.FLOW_TYPE_MESSENGER,
);

export function IntegrationNode({
  node,
  scopeVars,
}: IntegrationNodeProps): JSX.Element {
  const { grants } = scopeVars;
  const { payload } = node;
  const { httpRequestType = HTTPRequestMethod.Get } = payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextBreak>
              <Text weight="normal" text="Method" />
              <Text
                weight="semibold"
                text={`${httpRequestType}${grants.length > 0 ? ',' : ''}`}
              />
              {grants.length > 0 && (
                <>
                  <Text
                    weight="normal"
                    text={grants.length === 1 ? 'Variable' : 'Variables'}
                  />
                  <Text weight="semibold" text={grants.join(', ')} />
                </>
              )}
            </TextBreak>
          </>
        ),
        [httpRequestType, grants],
      )}
    </TextContent>
  );
}
